/* eslint-disable max-statements */
/* eslint-disable max-len */
/* eslint-disable complexity */
/**
 * @file 服务于通用业务解包和异常处理的 ky after response hook
 * @author sunyingying01
 */

import {type AfterResponseHook} from 'ky';
import type {JsonObject} from 'type-fest';
import {BizError} from '../Error/BizError';
import {handleUnauthorized} from '../auth/handleUnauthorized';
/**
 * 统一处理403无权限 - 跳转白名单页面
 */
function handleForbidden(): never {
    window.location.href = `${window.location.origin}/whitelist${window.location.search}`;
    throw new Error('Redirecting to whitelist'); // 永远不会执行到这里,但满足类型要求
}

// 企业子账号相关的 403 错误码，这类情况由 App 层展示提示页，不跳转白名单
const SUB_ACCOUNT_403_CODES = new Set([
    'ErrSubAccountNotManaged',
    'ErrSubAccountAccessDisabled',
    'ErrSubAccountIAMDeleted',
    'ErrSubAccountIAMPending'
]);

export const responseUnpackHook: AfterResponseHook = async (request, _options, response) => {
    const responseClone = response.clone();
    let responseJson: JsonObject = {};
    try {
        responseJson = await responseClone.json() as JsonObject;
    } catch {
        // 某些错误页（如404）可能返回HTML,忽略解析错误
        responseJson = {};
    }

    // 🟢 检查是否是 userinfo 接口 - 它需要在 App 层面自己处理 401/403
    const isUserInfoRequest = request.url.includes('/users/userinfo');

    // --- 统一401/403处理 - 所有接口都会被拦截 ---
    const is401 = response.status === 401
               || (responseJson['success'] === false && responseJson.code === 401);
    if (is401 && !isUserInfoRequest) {
        handleUnauthorized();
    }

    const is403 = response.status === 403
               || (responseJson['success'] === false && responseJson.code === 403);
    if (is403 && !isUserInfoRequest) {
        // 企业子账号相关错误码，让 App 层自己处理展示，不跳白名单
        const errCode = String(responseJson['error_code'] ?? responseJson['code'] ?? '');
        if (SUB_ACCOUNT_403_CODES.has(errCode)) {
            const errMessage = responseJson.message as string || responseJson.error as string || '无访问权限';
            window.dispatchEvent(new CustomEvent('sub-account-forbidden', {detail: {code: errCode, message: errMessage}}));
            throw new BizError(errMessage, {
                statusCode: 403,
                code: errCode,
                response: responseClone,
                data: responseJson
            });
        }
        handleForbidden();
    }

    // --- HTTP 状态异常 ---
    if (!response.ok) {
        const {status} = response;
        let errorMessage = responseJson.message as string;

        if (status === 400) {
            errorMessage = errorMessage || '请求参数错误，请检查输入内容';
            console.error('[400 Bad Request]', {
                url: request.url,
                message: errorMessage,
                data: responseJson
            });
        } else if (status === 404) {
            errorMessage = errorMessage || `请求地址不存在：${request.url}`;
        } else if (status >= 500) {
            errorMessage = errorMessage || '服务器内部错误，请稍后重试';
        } else {
            errorMessage = errorMessage || `请求失败（${status}）`;
        }

        throw new BizError(errorMessage, {
            statusCode: status,
            code: responseJson.code || status,
            response: responseClone,
            data: responseJson
        });
    }

    // --- 业务异常 ---
    if (responseJson['success'] === false) {
        const errorMessage = responseJson.message as string || '业务请求失败';

        throw new BizError(errorMessage, {
            statusCode: response.status,
            code: responseJson.code as number || 0,
            response: responseClone,
            data: responseJson
        });
    }

    // --- 正常返回 ---
    return new Response(JSON.stringify({
        code: responseJson.code || '0',
        message: responseJson.message || '',
        data: responseJson.data || {}
    }), {status: response.status});
};
